import { post, type ApiClientEvent } from '@aiptyg/api-client';

type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'fatal';

interface LogEntry {
  ts: number;
  level: LogLevel;
  category: string;
  event: string;
  message?: string;
  data?: Record<string, unknown>;
}

const MAX_ENTRIES = 300;
const UPLOAD_DEBOUNCE_MS = 30_000;
const entries: LogEntry[] = [];
let latestError: LogEntry | null = null;
let initialized = false;
let uploading = false;
let lastUploadAt = 0;

const SENSITIVE_KEYS = new Set([
  'authorization', 'Authorization',
  'token', 'accessToken', 'AccessToken', 'refreshToken', 'RefreshToken',
  'password', 'Password', 'confirmPassword',
  'code', 'verifyCode', 'verificationCode',
  'secret', 'anonSecret',
  'x-req-signature', 'X-Req-Signature',
  'x-req-nonce', 'X-Req-Nonce',
  'x-req-timestamp', 'X-Req-Timestamp',
  'x-req-body-hash', 'X-Req-Body-Hash',
  'signature', 'nonce', 'bodyHash',
]);

export function initAdminLogger(): void {
  if (initialized) return;
  initialized = true;

  window.addEventListener('error', (event) => {
    error('runtime', 'window_error', event.message, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });
  window.addEventListener('unhandledrejection', (event) => {
    error('runtime', 'unhandled_rejection', errorMessage(event.reason), {
      reason: errorMessage(event.reason),
    });
  });
}

export function handleApiClientEvent(event: ApiClientEvent): void {
  if (event.path === '/v1/public/diagnostics/client') {
    return;
  }
  const data = {
    method: event.method,
    path: event.path,
    requestId: event.requestId,
    status: event.status,
    duration: event.duration,
    signed: event.signed,
    hasAuth: event.hasAuth,
  };
  if (event.type === 'request_failure') {
    const level: LogLevel = event.status && event.status >= 500 ? 'error' : 'warn';
    log(level, 'api', event.type, event.message, data);
    scheduleUpload('api_failure');
  } else if (import.meta.env.DEV) {
    debug('api', event.type, undefined, data);
  }
}

export function debug(category: string, event: string, message?: string, data?: Record<string, unknown>): void {
  log('debug', category, event, message, data);
}

export function info(category: string, event: string, message?: string, data?: Record<string, unknown>): void {
  log('info', category, event, message, data);
}

export function warn(category: string, event: string, message?: string, data?: Record<string, unknown>): void {
  log('warn', category, event, message, data);
}

export function error(category: string, event: string, message?: string, data?: Record<string, unknown>): void {
  log('error', category, event, message, data);
  scheduleUpload('runtime_error');
}

function log(
  level: LogLevel,
  category: string,
  event: string,
  message?: string,
  data?: Record<string, unknown>,
): void {
  const entry: LogEntry = {
    ts: Date.now(),
    level,
    category,
    event,
    ...(message ? { message } : {}),
    ...(data ? { data: scrubObject(data) } : {}),
  };
  entries.push(entry);
  if (entries.length > MAX_ENTRIES) {
    entries.splice(0, entries.length - MAX_ENTRIES);
  }
  if (level === 'warn' || level === 'error' || level === 'fatal') {
    latestError = entry;
  }
}

function scheduleUpload(reason: string): void {
  if (uploading) return;
  const now = Date.now();
  if (now - lastUploadAt < UPLOAD_DEBOUNCE_MS) return;
  lastUploadAt = now;
  window.setTimeout(() => {
    void uploadDiagnostics(reason);
  }, 0);
}

async function uploadDiagnostics(reason: string): Promise<void> {
  if (uploading || entries.length === 0) return;
  uploading = true;
  try {
    const logs = entries.map(toUploadEntry);
    await post(
      '/v1/public/diagnostics/client',
      {
        sessionId: getSessionId(),
        platform: 'web-admin',
        appVersion: import.meta.env.VITE_APP_VERSION || '0.0.0',
        buildNumber: import.meta.env.VITE_GIT_COMMIT || 'unknown',
        updateId: null,
        environment: import.meta.env.DEV ? 'development' : 'production',
        route: window.location.pathname,
        uploadReason: reason,
        generatedAt: new Date().toISOString(),
        latestError: latestError ? toUploadEntry(latestError) : null,
        logs,
        device: {
          brand: 'browser',
          modelName: navigator.userAgent.slice(0, 120),
        },
        extra: { source: 'admin-web' },
      },
      { timeout: 30_000, anonymous: true },
    );
  } catch {
    // Diagnostics must not affect the admin console.
  } finally {
    uploading = false;
  }
}

function toUploadEntry(entry: LogEntry): Record<string, unknown> {
  return {
    ts: new Date(entry.ts).toISOString(),
    level: entry.level,
    category: entry.category,
    event: entry.event,
    message: entry.message,
    route: window.location.pathname,
    requestId: typeof entry.data?.requestId === 'string' ? entry.data.requestId : undefined,
    data: entry.data,
  };
}

function getSessionId(): string {
  const key = 'aiptyg_admin_log_session_id';
  const existing = window.sessionStorage.getItem(key);
  if (existing) return existing;
  const value = `admin_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  window.sessionStorage.setItem(key, value);
  return value;
}

function scrubObject(obj: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  Object.entries(obj).forEach(([key, value]) => {
    if (SENSITIVE_KEYS.has(key)) {
      result[key] = '[REDACTED]';
    } else if (Array.isArray(value)) {
      result[key] = value.map((item) =>
        typeof item === 'object' && item !== null
          ? scrubObject(item as Record<string, unknown>)
          : item,
      );
    } else if (typeof value === 'object' && value !== null) {
      result[key] = scrubObject(value as Record<string, unknown>);
    } else {
      result[key] = value;
    }
  });
  return result;
}

function errorMessage(value: unknown): string {
  if (value instanceof Error) return value.message;
  if (typeof value === 'string') return value;
  return String(value);
}
